import React, {useEffect, useState} from 'react';
import {
    DEFAULT_FEATURED_IMAGE_SETTINGS,
    FEATURED_IMAGE_FIELD_LIST
} from "../../constants/shopify-featured-image.constant";
import SelectColorTypeDetails from "../../../design/components/design-editor/settings/select-color-type-details";
import _ from "lodash";
import ProductImageSmall from "../../../pod/components/product-upload/product-image-small";
import RenderFeaturedImage from "./render-featured-image";
import {applyProductImageFeaturedSettings, resetImagePosition} from "../../utils/featured-image-util";
import {SAVE_BLOG_POST_TIME_DELAY} from "../../../content/constants/blog.constant";
import {updateSingleFeaturedImage} from "../../../content/services/featured-image-data-service";
import RenderFields from "../../../common/ui/render-fields";
import {REPEAT_IMAGE_FIELD_LIST} from "../../../design/constants/design.constant";
import DeleteItem from "../../../common/ui/delete-item";

const defaultSettings = DEFAULT_FEATURED_IMAGE_SETTINGS;

const FeaturedImageEditor = ({productImageList, selectedDesign, featuredImage, setFeaturedImage, productsList}) => {
    const [settings, setSettings] = useState(defaultSettings);
    const [initialStep, setInitialStep] = useState(true);
    const [selectedImages, setSelectedImages] = useState([]);
    let [changesCount, setChangesCount] = useState(0);
    const [currentSelectedImage, setCurrentSelectedImage] = useState(null);

    useEffect(() => {
        if (initialStep) {
            init();
        }
    }, [initialStep]);

    useEffect(() => {
        if (settings || selectedImages) {
            incrementChangesCount();
        }
    }, [settings, selectedImages]);

    const init = () => {
        let existingSettings = _.cloneDeep(defaultSettings);

        if (featuredImage) {
            if (!_.isEmpty(featuredImage.images)) {
                setSelectedImages(_.orderBy(featuredImage.images, 'props.x', 'asc'));
                setCurrentSelectedImage(featuredImage.images[0]);
            }

            if (!_.isEmpty(featuredImage.settings)) {
                existingSettings = featuredImage.settings;
            }
        }

        existingSettings.handle = _.kebabCase(selectedDesign?.name);
        setSettings(existingSettings);
    }

    const incrementChangesCount = () => {
        setChangesCount(++changesCount);
    }

    const resetChangesCount = () => {
        setChangesCount(0);
    }

    useEffect(() => {
        const timeoutId =
            setTimeout(async () => {
                if (changesCount > 1) {
                    resetChangesCount();
                    saveFeaturedImage();
                }
            }, SAVE_BLOG_POST_TIME_DELAY);

        return () => clearTimeout(timeoutId);
    }, [changesCount]);

    const saveFeaturedImage = async () => {
        const existingFeaturedImage = {...featuredImage};
        existingFeaturedImage.settings = {...settings};
        existingFeaturedImage.images = [...selectedImages];

        setFeaturedImage(existingFeaturedImage);
        await updateSingleFeaturedImage(existingFeaturedImage);
    }

    const handleSettingsChanges = (event, childProperty) => {
        const {name, value, type} = event.target;
        const existingSettings = {...settings} || _.cloneDeep(defaultSettings);

        if (childProperty === 'product') {
            const productConfig = existingSettings.product;
            if (type === 'checkbox') {
                productConfig[name] = !productConfig[name];
            } else if (type === 'number') {
                productConfig[name] = _.parseInt(value);
            } else {
                productConfig[name] = value;
            }

            existingSettings.product = productConfig;
            applyProductImageFeaturedSettings(existingSettings, productImageList);
        } else {
            if (type === 'checkbox') {
                existingSettings[name] = !existingSettings[name];
            } else {
                existingSettings[name] = value;
            }
        }

        setSettings(existingSettings);
    }

    const handleClearColorInSettings = (attributeList) => {
        const existingSettings = {...settings} || defaultSettings;

        _.forEach(attributeList, attribute => {
            existingSettings[attribute] = '';
        });

        setSettings(existingSettings);
    }

    const handleSelectedProductImage = (productImage) => {
        const existingSelectedImages = [...selectedImages];

        const existingProductImage = existingSelectedImages?.find((selectedProductImage) => selectedProductImage?.id === productImage?.id);
        if (!existingProductImage) {
            resetImagePosition(productImage, settings);
            existingSelectedImages.push(productImage);
            setSelectedImages(existingSelectedImages);
            setCurrentSelectedImage(productImage);
        } else {
            const filteredProductImageList = existingSelectedImages?.filter((selectedProductImage) => selectedProductImage?.id !== productImage?.id);
            setSelectedImages(filteredProductImageList);
            setCurrentSelectedImage(filteredProductImageList[0]);
        }
    }

    const handleImageDetailChanges = (event, imageItem) => {
        const {name, value, type} = event.target;
        const existingSelectedImages = [...selectedImages];
        const itemIndex = _.findIndex(existingSelectedImages, ['id', imageItem.id]);

        const imageSettings = imageItem.settings || {};
        const imageProps = imageItem.props;

        if (type === 'number') {
            imageSettings[name] = _.parseInt(value);
        } else {
            imageSettings[name] = value;
        }

        /* if (name === 'size') {
             let size = _.parseInt(value);
             if (_.isNaN(size)) {
                 size = 100;
             }

             imageProps.width = imageItem.imageWidth * size / 100;
             imageProps.height = imageItem.imageHeight * size / 100;
         } else*/
        if (name === 'fillPatternOffsetXPercentage') {
            let fillPatternOffsetXPercentage = _.parseInt(value);
            if (_.isNaN(fillPatternOffsetXPercentage)) {
                fillPatternOffsetXPercentage = 0;
            }

            imageProps.x = imageProps.width * fillPatternOffsetXPercentage / 100;
        } else if (name === 'fillPatternOffsetYPercentage') {
            let fillPatternOffsetYPercentage = _.parseInt(value);
            if (_.isNaN(fillPatternOffsetYPercentage)) {
                fillPatternOffsetYPercentage = 0;
            }

            imageProps.y = imageProps.width * fillPatternOffsetYPercentage / 100;
        }

        imageItem.settings = imageSettings;
        imageItem.props = imageProps;

        setCurrentSelectedImage(imageItem);
        existingSelectedImages[itemIndex] = imageItem;
        setSelectedImages(_.orderBy(existingSelectedImages, 'props.x', 'asc'));
    }

    return (
        <>

            <div className="flex justify-center w-full p-4">

                <div className="flex justify-start w-3/4">
                    <RenderFeaturedImage
                        selectedImages={selectedImages}
                        setSelectedImages={setSelectedImages}
                        settings={settings}/>
                </div>

                <div
                    className="w-fit instagram-image-settings-container overflow-y-scroll border border-gray-400 bg-gray-200 rounded-lg mt-4 ml-8 pt-4 px-4">
                    {settings &&
                        <>
                            <SelectColorTypeDetails
                                label={'Background Color & Shade'}
                                parentForm={settings?.background}
                                colorAttribute={'background'}
                                colorKeyAttribute={'backgroundColorKey'}
                                colorCodeKeyAttribute={'backgroundColorCodeKey'}
                                settings={settings}
                                handleColorDetailsChanges={handleSettingsChanges}
                                handleClearColorDetailsChanges={handleClearColorInSettings}/>
                        </>
                    }

                    <div
                        className="h-fit flex flex-wrap content-start justify-center overflow-y-scroll mt-8 mx-2 p-4 border border-gray-400 bg-gray-400 rounded-lg">
                        {selectedImages &&
                            <>
                                {selectedImages?.map((productImage, productImageIndex) => (
                                    <ProductImageSmall
                                        afterImageSelected={(event) => {
                                            setCurrentSelectedImage(productImage);
                                        }}
                                        dimension={100}
                                        isSelected={currentSelectedImage?.id === productImage?.id}
                                        key={`current-selected-product-image-${productImageIndex}`}
                                        imageUrl={productImage?.url}/>
                                ))}
                            </>
                        }
                    </div>


                    {currentSelectedImage &&
                        <>
                            <div className={'ml-2 mt-4 w-full flex items-center'}>
                                <span className={'font-semibold mr-2'}>Current Selected Image</span>
                                <DeleteItem
                                    handleDeleteItem={() => {
                                        handleSelectedProductImage(currentSelectedImage)
                                    }}
                                    iconSize={'h-3 w-3'}
                                    showIcon={false}/>
                            </div>
                            <div className="flex flex-wrap justify-start mt-2">
                                <RenderFields
                                    parentForm={currentSelectedImage?.settings || {}}
                                    handleDetailChanges={(event) => {
                                        handleImageDetailChanges(event, currentSelectedImage);
                                    }}
                                    fieldList={_.groupBy(FEATURED_IMAGE_FIELD_LIST, 'group')['dimension']}
                                    enableEdit={true}
                                    fieldClassName={'w-1/2'}/>
                            </div>
                        </>
                    }


                    <div
                        className="h-fit flex flex-wrap content-start justify-center overflow-y-scroll mt-4 mx-2 p-4 border border-gray-400 bg-gray-400 rounded-lg">
                        {!productsList &&
                            <>
                                {productImageList?.map((productImage, productImageIndex) => (
                                    <ProductImageSmall
                                        afterImageSelected={(event) => {
                                            handleSelectedProductImage(productImage);
                                        }}
                                        dimension={100}
                                        isSelected={selectedImages?.find((selectedFeaturedImage) => selectedFeaturedImage?.id === productImage?.id)}
                                        key={`selected-product-image-${productImageIndex}`}
                                        imageUrl={productImage?.url}/>
                                ))}
                            </>
                        }

                        {productsList?.length > 0 &&
                            <>
                                {productsList?.map((product, productIndex) => (
                                    <div key={productIndex}>
                                        <div
                                            className={'text-lg font-semibold text-white mb-2'}>{product?.title}</div>
                                        <div className="flex flex-wrap justify-start mb-6">
                                            {product?.images?.map((productImage, productImageIndex) => (
                                                <ProductImageSmall
                                                    afterImageSelected={(event) => {
                                                        handleSelectedProductImage(productImage)
                                                    }}
                                                    dimension={100}
                                                    isSelected={selectedImages?.find((selectedFeaturedImage) => selectedFeaturedImage?.id === productImage?.id)}
                                                    key={`selected-product-image-${productImageIndex}`}
                                                    imageUrl={productImage?.url}/>
                                            ))}
                                        </div>
                                    </div>
                                ))}
                            </>
                        }
                    </div>
                </div>

            </div>

        </>
    )
}

export default FeaturedImageEditor
